import React, { useRef, useEffect, useCallback, useState } from 'react';
import { GroupedVirtuoso } from 'react-virtuoso';
import { useDispatch, useSelector } from 'react-redux';
import {
  _,
  dayjs,
  ReactSVG,
  ToastMessage,
  Trans,
  useTranslation,
} from '~/dependencies';
import { Message } from '~/interfaces/conversations';
import { RootState } from '~/store/config';
import * as actions from '~/store/actions';
import { DeleteMessageIcon, NoSearchResultIcon } from '~/assets/svgIcons';
import { colors } from '~/layout';
import { MIN_SEARCH_CARACTERS } from '../..';
import ConfirmationBox from '../../../components/ConfirmationBox';
import Row from '../Row';
import { Container, Empty, SearchFoundResults, Section } from './styles';

const groupMessagesByDate = (
  messageList: Record<string, any>,
  userTimeZone: string
) => {
  let messages;

  messages = _(messageList)
    .orderBy((e: Message) => e.createdAt)
    .values();

  messages = messages.map((message: Message) => ({
    ...message,
    createdAt:
      dayjs(message.createdAt)
        .tz(userTimeZone)
        .format('ddd DD MMMM') ===
      dayjs(new Date())
        .tz(userTimeZone)
        .format('ddd DD MMMM')
        ? `Today`
        : `${dayjs(message.createdAt)
            .tz(userTimeZone)
            .format('ddd DD MMMM')}`,
  }));

  return _(messages)
    .groupBy((e: Message) => e.createdAt)
    .value();
};

const EmptyConversation = () => {
  const { t } = useTranslation();

  return (
    <Empty data-test-id="messages-empty-state">
      <ReactSVG src={NoSearchResultIcon} className="icon" />
      <span className="title">{t('screens.conversation.noResultFound')}</span>
      <span className="sub-title ">
        {t('screens.conversation.noResultFoundSubTitle')}
      </span>
    </Empty>
  );
};

type ListProps = {
  data: Record<string, any>;
  searchValue: string;
};

const List = ({ data, searchValue }: ListProps) => {
  // REF's
  const virtuoso = useRef(null);

  // HOOKS
  const dispatch = useDispatch();
  const { t } = useTranslation();

  // STATE's
  const [openDialog, setOpenDialog] = useState(false);
  const [messageId, setMessageId] = useState<string>('');
  const [conversationId, setConversationId] = useState<string>('');

  // SELECTOR's
  const isLoading = useSelector(
    (state: RootState) => state.conversation?.isLoading
  );
  const userTimeZone = useSelector(
    (state: RootState) => state.auth.userTimeZone
  );

  const groupedMessages = groupMessagesByDate(data, userTimeZone);
  const groupCounts = Object.values(groupedMessages).map(
    e => (e as Message[]).length
  );
  const groups = Object.keys(groupedMessages);

  const renderGroup = (index: number) => (
    <Section data-test-id="message-date-separator">
      <div className="line" />
      <span>{groups[index]}</span>
      <div className="line" />
    </Section>
  );

  const scrollToLastItem = useCallback((behavior: string = 'auto') => {
    setTimeout(() => {
      if (virtuoso?.current) {
        // @ts-ignore
        virtuoso.current.scrollToIndex?.({
          index: Number.MAX_SAFE_INTEGER,
          align: 'smooth',
          behavior,
        });
      }
    }, 100);
  }, []);

  /**
   * Callback when the user click on the cancel message
   * Display the confirmation box
   */
  const handleOnClickCancelMessage = (scheduledMessageInfo: any) => {
    const { messageId: id, conversationId: convId } = scheduledMessageInfo;
    setConversationId(convId);
    setMessageId(id);
    setOpenDialog(true);
  };

  const handleCloseConfirmationBox = () => {
    setOpenDialog(false);
    setMessageId('');
  };

  const handleConfirmCancelScheduledMessage = async () => {
    try {
      await dispatch(
        actions.cancelConversationMessage(conversationId, messageId)
      );
    } catch (err) {
      ToastMessage(
        'error',
        t('screens.conversation.failureToCancelMessage'),
        7000
      );
    } finally {
      handleCloseConfirmationBox();
    }
  };

  // Redirect the user to the last message
  useEffect(() => {
    if (data.length > 1 && virtuoso.current) {
      scrollToLastItem();
    }
  }, [data, scrollToLastItem]);

  return (
    <Container>
      <ConfirmationBox
        title={t('screens.conversation.cancelMessageAction')}
        open={openDialog}
        close={handleCloseConfirmationBox}
        icon={DeleteMessageIcon}
        isLoading={isLoading}
        btnColor={colors.yellow04}
        btnName={t('screens.conversation.btnDoIt')}
        handleAction={handleConfirmCancelScheduledMessage}
      />

      {data.length > 0 && searchValue.length > MIN_SEARCH_CARACTERS ? (
        <SearchFoundResults data-test-id="messages-search-results">
          <span>
            <Trans
              i18nKey="screens.conversation.searchLenResult"
              values={{ itemLen: data.length }}
              components={{ bold: <strong className="text-blue-500" /> }}
            />
          </span>
        </SearchFoundResults>
      ) : null}

      {groupCounts.length > 0 ? (
        <GroupedVirtuoso
          ref={virtuoso}
          groupCounts={groupCounts}
          group={index => renderGroup(index)}
          item={index => (
            <Row
              item={data[index]}
              userTz={userTimeZone}
              searchValue={searchValue}
              cancelMessage={handleOnClickCancelMessage}
            />
          )}
          style={{ flex: 1 }}
        />
      ) : null}
      {data.length === 0 && !isLoading ? <EmptyConversation /> : null}
    </Container>
  );
};

export default List;
